import React from 'react';
import getName from '../../../../utils/getName';
import { getIntl } from '../../../../utils/intl';
import getDateString from '../../../../utils/getDateString';
import { useProfilesTabNavigation } from '../../../../utils/appNavigationNames';
import urls from '../../../../utils/urls';
import { APITypes } from '../../../../primary-record-types';
import { isInsurance, isProvider, isSupport } from '../../../../utils/careTeamUtils';
import Text from '../../../v2BaseComponents/Text';
import { ContentStack } from '../../../v2BaseComponents/Stack';
import * as Card from '../../../v2BaseComponents/Card';
import { getFallbackFromName } from '../../../v2BaseComponents/Avatar';
import { getCollaboratorName, isOrganizationCollaborator } from '../../../../utils/collaboratorUtils';

const intl = getIntl('profile-invitation-list', {
    invitationSent: 'Invitation Sent {dateTime}',
    support: 'Support, {role}',
    provider: 'Provider, {providerType} {specialty}',
    insurance: 'Insurance',
    lastAccess: 'Last access {dateTime}',
    emptyStateMessage:
        'Invite others to access this Medical Profile. You will have the opportunity to control their view and permissions.',
    sharedAccess: 'Collaborators',
    pendingInvitations: 'Pending Collaborator Invitations',
    professional: 'Professional',
});

export type CollaboratorAndInviteListOwnProps = {
    profileId: string;
    collaboratorInvites: APITypes.medicalProfileService.CollaboratorInviteListItem[];
    collaborators: APITypes.medicalProfileService.MedicalProfileCollaborator[];
};

export type CollaboratorAndInviteListProps = CollaboratorAndInviteListOwnProps;

const CollaboratorAndInviteList: React.FC<CollaboratorAndInviteListProps> = (props) => {
    const profileNavigation = useProfilesTabNavigation();
    const shouldShowEmptyStateMessage = !props.collaborators?.length && !props.collaboratorInvites?.length;

    const getCollaboratorSubText = (collaborator: APITypes.medicalProfileService.MedicalProfileCollaborator) => {
        if (!collaborator.associatedCareTeamMember) return null;

        if (isSupport(collaborator.associatedCareTeamMember)) {
            return intl('support', { role: collaborator.associatedCareTeamMember.contactDetails.role || '' });
        } else if (isProvider(collaborator.associatedCareTeamMember)) {
            return intl('provider', {
                providerType: collaborator.associatedCareTeamMember.metadata.providerType,
                specialty: collaborator.associatedCareTeamMember.metadata.providerSpecialty || '',
            });
        } else if (isInsurance(collaborator.associatedCareTeamMember)) {
            return intl('insurance');
        }
    };

    if (shouldShowEmptyStateMessage) {
        return <Text variant="body-1">{intl('emptyStateMessage')}</Text>;
    }

    return (
        <ContentStack>
            {props.collaborators.length ? <Text variant="h4">{intl('sharedAccess')}</Text> : null}
            {props.collaborators.map((collaborator) => (
                <Card.Card
                    key={collaborator.medicalProfileCollaboratorId}
                    testID="collaborator-list-item"
                    onPress={() => {
                        profileNavigation.navigate('collaborator', {
                            profileId: props.profileId,
                            collaboratorId: collaborator.medicalProfileCollaboratorId,
                        });
                    }}
                >
                    <Card.CardLeftContent
                        leftContent={
                            <Card.CardLoadableAvatar
                                size="md"
                                avatarUrl={urls.api.collaborators.collaboratorAvatar(
                                    props.profileId,
                                    collaborator.medicalProfileCollaboratorId,
                                )}
                                fallbackName={getFallbackFromName(getCollaboratorName(collaborator))}
                            />
                        }
                    >
                        <Card.CardContent>
                            <Card.CardHeader>{getCollaboratorName(collaborator)}</Card.CardHeader>
                            <Card.CardBody>{getCollaboratorSubText(collaborator)}</Card.CardBody>
                            {collaborator.lastProfileViewedDateTime ? (
                                <Card.CardBody2>
                                    {intl('lastAccess', {
                                        dateTime: getDateString(collaborator.lastProfileViewedDateTime, 'date-time'),
                                    })}
                                </Card.CardBody2>
                            ) : null}
                            {isOrganizationCollaborator(collaborator) ? (
                                <Card.CardChip title={intl('professional')} />
                            ) : null}
                        </Card.CardContent>
                    </Card.CardLeftContent>
                </Card.Card>
            ))}
            {props.collaboratorInvites.length ? <Text variant="h4">{intl('pendingInvitations')}</Text> : null}
            {props.collaboratorInvites.map((invite) => (
                <Card.Card
                    key={invite.medicalProfileCollaboratorInviteId}
                    testID="collaborator-invite-list-item"
                    onPress={() =>
                        profileNavigation.navigate('invite-details', {
                            profileId: props.profileId,
                            invitationId: invite.medicalProfileCollaboratorInviteId,
                        })
                    }
                >
                    <Card.CardLeftContent leftContent={<Card.CardIconAvatar icon="Send" />}>
                        <Card.CardContent>
                            <Card.CardHeader>
                                {getName({ firstName: invite.inviteeFirstName, lastName: invite.inviteeLastName })}
                            </Card.CardHeader>
                            <Card.CardBody>{invite.email}</Card.CardBody>
                            <Card.CardBody2>
                                {intl('invitationSent', { dateTime: getDateString(invite.createDate, 'date-time') })}
                            </Card.CardBody2>
                        </Card.CardContent>
                    </Card.CardLeftContent>
                </Card.Card>
            ))}
        </ContentStack>
    );
};

export default React.memo(CollaboratorAndInviteList);
